// Copyright 2023 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { Modal } from "antd";
import { ExclamationCircleFilled } from "@ant-design/icons";
import i18next from "i18next";
import { clearWeb3AuthToken } from "../auth/Web3Auth";
import * as Conf from "../Conf";
import * as Setting from "../Setting";
import * as AuthBackend from "../auth/AuthBackend";

const { fetch: originalFetch } = window;

// === token 过期处理 ===
const handleTokenExpiration = () => {
  if (window.isHandlingTokenExpiration) return;
  window.isHandlingTokenExpiration = true;
  Modal.error({
    title: i18next.t("self:Session Expired"),
    content: i18next.t("self:Your session has expired. Please login again."),
    okText: i18next.t("general:OK"),
    onOk: () => {
      window.isHandlingTokenExpiration = false;
      const signinUrl = localStorage.getItem("signinUrl");
      AuthBackend.logout().then((res) => {
        if (res.status === "ok") {
          clearWeb3AuthToken();
          Setting.setToken("");
          Setting.showMessage("success", i18next.t("application:Logged out successfully"));
          const redirectUri = res.data2;
          if (redirectUri) {
            Setting.goToLink(redirectUri);
          } else {
            Setting.goToLink(`${window.location.origin}${signinUrl}`);
          }
        } else {
          Setting.showMessage("error", `Failed to log out: ${res.msg}`);
        }
      });
    },
  });
};

// === demo 模式错误拦截 ===
const demoModeCallback = (res) => {
  res.json().then((data) => {
    if (Setting.isResponseDenied(data)) {
      Modal.confirm({
        title: i18next.t("general:This is a read-only demo site!"),
        icon: <ExclamationCircleFilled />,
        content: i18next.t("general:Go to writable demo site?"),
        okText: i18next.t("general:OK"),
        cancelText: i18next.t("general:Cancel"),
        onOk() {
          Setting.openLink(
            `https://demo.casdoor.com${location.pathname}${location.search}?username=built-in/admin&password=123`
          );
        },
      });
    }
  });
};

const requestFilters = [];
const responseFilters = [];

if (Conf.IsDemoMode) responseFilters.push(demoModeCallback);
responseFilters.push((response) => {
  if (response.status === 401) {
    console.warn("401 Unauthorized from:", response.url);
    handleTokenExpiration();
  }
});

const ignoredUrlPatterns = [
  "/main/service/webSearch/webSearch",
  "/main/service/translate/translate",
  "/main/service/file/documentExtraction",
  "/main/service/data-extract/documentProcessing",
  "/main/service/dataset/getKnowLedgeDBList",
  "/main/service/assistant-chat/sendTextMessage",
  "/main/service/assistant-chat/getQuestionGuide",
  "/main/service/assistant-chat/getConversationListByProjectId",
  "/main/service/assistant-chat/uploadFileAndExtract",
  "/main/service/assistant-chat/saveConversationTitle",
  "/main/service/user/getUserList",
  "/main/service/team-prompt/getTeamPromptEnabledTreeList",
  "/main/service/task-log/getTaskDetail"
];

const shouldIgnoreUrl = (url, option = {}) => {
  if (typeof url !== "string") return false;
  try {
    const urlObj = new URL(url, window.location.origin);
    return ignoredUrlPatterns.some((pattern) =>
      urlObj.pathname === pattern || urlObj.pathname.startsWith(pattern + "/")
    );
  } catch (e) {
    console.error("Error processing URL:", e, "URL:", url);
    return false;
  }
};

let activeRequestCount = 0;
let isRequestInProgress = false;
const loadingChangeCallbacks = new Set();
export function registerLoadingCallback(callback) {
  loadingChangeCallbacks.add(callback);
  return function unregister() {
    loadingChangeCallbacks.delete(callback);
  };
}
const updateLoadingState = () => {
  activeRequestCount = Math.max(0, activeRequestCount);
  const shouldBeLoading = activeRequestCount > 0;
  if (Setting.Gloading !== shouldBeLoading) {
    Setting.setGloading(shouldBeLoading);
    loadingChangeCallbacks.forEach((cb) => {
      try {
        cb(shouldBeLoading);
      } catch (e) {
        console.error("Error in loading callback:", e);
      }
    });
  }
};
const resetLoadingState = () => {
  if (isRequestInProgress) return;
  activeRequestCount = 0;
  updateLoadingState();
};

// === 拦截 fetch 实现统一 JSON 安全封装 ===
// 防止热更新时重复注册
if (window._fetchInterceptorRegistered) {
  console.log("Fetch interceptor already registered, skipping...");
} else {
  window._fetchInterceptorRegistered = true;
  
  window.fetch = async (url, option = {}) => {
    // 添加调试日志
    // if (process.env.NODE_ENV === 'development') {
    //   console.log('Fetch interceptor called:', {
    //     url,
    //     method: option.method || 'GET',
    //     hasBody: !!option.body,
    //     headers: option.headers
    //   });
    // }
    
    const shouldTrack = !shouldIgnoreUrl(url, option);
    const timeout = option.timeout;
    const onTimeout = option.onTimeout;
    const controller = new AbortController();
    const finalOptions = {
      ...option,
      signal: controller.signal,
    };

    let timeoutId;
    if (timeout) {
      timeoutId = setTimeout(() => {
        controller.abort();
        if (typeof onTimeout === "function") onTimeout();
      }, timeout);
    }

    if (shouldTrack) {
      isRequestInProgress = true;
      activeRequestCount++;
      updateLoadingState();
    }

    requestFilters.forEach((f) => f(url, finalOptions));

    let response;
    try {
      response = await originalFetch(url, finalOptions);
      clearTimeout(timeoutId);

      if (response.status === 524) {
        console.warn("524 Timeout from:", url);
        Modal.error({
          title: i18next.t("general:Operation Timeout"),
          content: i18next.t("general:The server took too long to respond. Please try again later."),
          okText: i18next.t("general:OK"),
        });
      }

      if (typeof url === "string" && !url.startsWith("/api/get-organizations")) {
        responseFilters.forEach((f) => f(response.clone()));
      }

      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        let finalText = "{}";
        try {
          const text = await response.text();
          JSON.parse(text);
          finalText = text;
        } catch {
          console.warn("Invalid JSON or empty response replaced with '{}':", url);
        }
        response = new Response(finalText, {
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
        });
      }

      // 统一保证返回的 response.json() 不会报错
      const clonedText = await response.text();
      let safeText = "{}";
      try {
        if (clonedText && typeof clonedText === "string") {
          JSON.parse(clonedText);
          safeText = clonedText;
        } else {
          console.warn("Empty or invalid JSON response. Using fallback '{}' for:", url);
        }
      } catch (e) {
        console.warn("Failed to parse JSON response, replaced with '{}':", url, e.message);
      }
      response = new Response(safeText, {
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
      });
      return response;
    } catch (error) {
      if (error.name === "AbortError") {
        console.warn("Request aborted due to timeout:", url);
        return new Response(
          JSON.stringify({ status: "cancelled", message: "Request was cancelled due to timeout" }),
          {
            status: 499,
            headers: { "Content-Type": "application/json" },
          }
        );
      }
      console.error("Fetch error for URL:", url, error);
      throw error;
    } finally {
      clearTimeout(timeoutId);
      if (shouldTrack) {
        isRequestInProgress = false;
        activeRequestCount = Math.max(0, activeRequestCount - 1);
        updateLoadingState();
      }
    }
  };

  if (window.fetch !== originalFetch) {
    console.log("Fetch interceptor is active");
  } else {
    console.error("Fetch interceptor failed to install!");
  }
}

let lastPathname = window.location.pathname;
const observer = new MutationObserver(() => {
  const currentPathname = window.location.pathname;
  if (currentPathname !== lastPathname) {
    lastPathname = currentPathname;
    resetLoadingState();
  }
});
observer.observe(document, { subtree: true, childList: true });

document.addEventListener("visibilitychange", () => {
  if (document.hidden) resetLoadingState();
});
window.addEventListener("beforeunload", resetLoadingState);
window.addEventListener("online", resetLoadingState);
window.addEventListener("offline", resetLoadingState);
setInterval(() => {
  if (Setting.Gloading && activeRequestCount === 0 && !isRequestInProgress) {
    console.warn("Detected inconsistent loading state, resetting...");
    resetLoadingState();
  }
}, 5000)